// @flow
import type { BusinessLocationRecord } from "./records/BusinessLocationRecord";
import queryString from "query-string";

import type { BusinessLocationHit } from "shared/utilities/searchBusinessLocations";
import type { EventContent } from "redux/api-types/EventContent";
import type { EventTemplate } from "redux/api-types/EventTemplate";
import type { EventCreateRequest } from "redux/api-types/EventCreate";
import type {
  simpleReduxAction,
  apiErrorParams,
  apiErrorAction,
} from "shared/constants/flowTypes";
import type { callApiReturnType } from "redux/middleware/api";
import { CALL_API } from "redux/middleware/api";

import {
  INSTRUCTOR_LOCATIONS_REQUEST,
  INSTRUCTOR_LOCATIONS_SUCCESS,
  INSTRUCTOR_LOCATIONS_ERROR,
  EVENT_CONTENT_REQUEST,
  EVENT_CONTENT_REQUEST_SUCCESS,
  EVENT_CONTENT_REQUEST_ERROR,
  TEMPLATES_REQUEST,
  TEMPLATES_REQUEST_SUCCESS,
  TEMPLATES_REQUEST_ERROR,
  POST_CABELAS_CLASSES_REQUEST,
  POST_CABELAS_CLASSES_ROW_RESULT,
  POST_CABELAS_CLASS_SUCCESS,
  POST_CABELAS_CLASS_ERROR,
  TIMEZONE_LOOKUP,
  TIMEZONE_LOOKUP_REQUEST,
  TIMEZONE_LOOKUP_SUCCESS,
  TIMEZONE_LOOKUP_ERROR,
  TIMEZONE_LOOKUP_CLEAR,
} from "./createCabelasClassActions";
import { EVENTS_API_ENDPOINTS } from "./constants";

export type EventContentResponse = {
  data: Array<EventContent>,
};

export type receiveEventContentAction = {
  type: string,
  payload: EventContentResponse,
  meta: {
    receivedAt: number,
  },
};

export type TemplatesResponse = {
  data: Array<EventTemplate>,
};

export type receiveTemplatesAction = {
  type: string,
  payload: TemplatesResponse,
  meta: {
    receivedAt: number,
  },
};

export type CabelasClassBatchEvent = {
  event: EventCreateRequest,
  sourceRowNumber?: number,
  sourceLabel?: string,
  batchIndex?: number, // original submission position for retries
};

export type CabelasClassBatchResult = {
  batchIndex: number,
  sourceRowNumber?: number,
  sourceLabel?: string,
  startDate?: string,
  isCreated: boolean,
  statusCode?: number,
  isRetryable: boolean,
  message?: string,
};

export type requestPostCabelasClassesAction = {
  type: string,
  payload: {
    events: Array<CabelasClassBatchEvent>,
    isRetry: boolean,
  },
};

export type postCabelasClassesRowResultAction = {
  type: string,
  payload: { result: CabelasClassBatchResult },
};

export type PostCabelasClassSuccessAction = {
  type: string,
  payload: {
    event: mixed,
  },
  meta: {
    receivedAt: number,
  },
};

export const requestEventContent = (): simpleReduxAction => ({
  type: EVENT_CONTENT_REQUEST,
});

export const receiveEventContent = (
  json: EventContentResponse
): receiveEventContentAction => ({
  type: EVENT_CONTENT_REQUEST_SUCCESS,
  payload: json,
  meta: {
    receivedAt: Date.now(),
  },
});

export const requestEventContentError = (
  error: apiErrorParams
): apiErrorAction => ({
  type: EVENT_CONTENT_REQUEST_ERROR,
  error: true,
  payload: {
    errorMessage: undefined,
    errors: [],
    ...error,
  },
  meta: {
    receivedAt: Date.now(),
  },
});

export const requestTemplates = (): simpleReduxAction => ({
  type: TEMPLATES_REQUEST,
});

export const receiveTemplates = (
  json: TemplatesResponse
): receiveTemplatesAction => ({
  type: TEMPLATES_REQUEST_SUCCESS,
  payload: json,
  meta: {
    receivedAt: Date.now(),
  },
});

export const requestTemplatesError = (
  error: apiErrorParams
): apiErrorAction => ({
  type: TEMPLATES_REQUEST_ERROR,
  error: true,
  payload: {
    errorMessage: undefined,
    errors: [],
    ...error,
  },
  meta: {
    receivedAt: Date.now(),
  },
});

export const requestPostCabelasClasses = (
  events: Array<CabelasClassBatchEvent>,
  isRetry?: boolean = false
): requestPostCabelasClassesAction => ({
  type: POST_CABELAS_CLASSES_REQUEST,
  payload: {
    events,
    isRetry,
  },
});

export const postCabelasClassesRowResult = (
  result: CabelasClassBatchResult
): postCabelasClassesRowResultAction => ({
  type: POST_CABELAS_CLASSES_ROW_RESULT,
  payload: { result },
});

export const postCabelasClassSuccess = (
  json: mixed
): PostCabelasClassSuccessAction => ({
  type: POST_CABELAS_CLASS_SUCCESS,
  payload: {
    event: json,
  },
  meta: {
    receivedAt: Date.now(),
  },
});

export const postCabelasClassError = (
  error: apiErrorParams
): apiErrorAction => ({
  type: POST_CABELAS_CLASS_ERROR,
  error: true,
  payload: {
    errorMessage: undefined,
    errors: [],
    ...error,
  },
  meta: {
    receivedAt: Date.now(),
  },
});

export const getEventContent = (businessId: string): callApiReturnType => ({
  type: CALL_API,
  payload: {
    method: "GET",
    endpoint: `${EVENTS_API_ENDPOINTS.EVENT_CONTENT}?${queryString.stringify({
      "filter[businessId][_eq]": businessId,
    })}`,
    actions: {
      request: requestEventContent,
      success: receiveEventContent,
      failure: requestEventContentError,
    },
    disableErrorToast: true,
    toasts: {
      failure: "There was a problem loading event content options",
    },
  },
});

export const getTemplates = (businessId: string): callApiReturnType => ({
  type: CALL_API,
  payload: {
    method: "GET",
    endpoint: EVENTS_API_ENDPOINTS.BUSINESS_TEMPLATES(businessId),
    actions: {
      request: requestTemplates,
      success: receiveTemplates,
      failure: requestTemplatesError,
    },
    disableErrorToast: true,
    toasts: {
      failure: "There was a problem loading pricing templates",
    },
  },
});

export type lookupLocationTimezoneAction = {
  type: string,
  payload: {
    location: ?BusinessLocationHit,
    form: string,
  },
};

export const lookupLocationTimezone = (
  location: ?BusinessLocationHit,
  form: string
): lookupLocationTimezoneAction => ({
  type: TIMEZONE_LOOKUP,
  payload: {
    location,
    form,
  },
});

export const requestTimezoneLookup = (): simpleReduxAction => ({
  type: TIMEZONE_LOOKUP_REQUEST,
});

export const receiveTimezoneLookup = (
  timezone: string
): {
  type: string,
  payload: { timezone: string },
} => ({
  type: TIMEZONE_LOOKUP_SUCCESS,
  payload: { timezone },
});

export const timezoneLookupError = (error: apiErrorParams): apiErrorAction => ({
  type: TIMEZONE_LOOKUP_ERROR,
  error: true,
  payload: {
    errorMessage: undefined,
    errors: [],
    ...error,
  },
  meta: {
    receivedAt: Date.now(),
  },
});

export const clearTimezoneLookup = (): simpleReduxAction => ({
  type: TIMEZONE_LOOKUP_CLEAR,
});

export const postCabelasClasses = (
  events: Array<CabelasClassBatchEvent>,
  isRetry?: boolean = false
): requestPostCabelasClassesAction =>
  requestPostCabelasClasses(events, isRetry);

export type getInstructorLocationsAction = {
  type: string,
  payload: { businessId: string },
};

export const getInstructorLocations = (
  businessId: string
): getInstructorLocationsAction => ({
  type: INSTRUCTOR_LOCATIONS_REQUEST,
  payload: { businessId },
});

export type receiveInstructorLocationsAction = {
  type: string,
  payload: { locations: Array<BusinessLocationRecord> },
};

export const receiveInstructorLocations = (
  locations: Array<BusinessLocationRecord>
): receiveInstructorLocationsAction => ({
  type: INSTRUCTOR_LOCATIONS_SUCCESS,
  payload: { locations },
});

export const instructorLocationsError = (
  error: apiErrorParams
): apiErrorAction => ({
  type: INSTRUCTOR_LOCATIONS_ERROR,
  error: true,
  payload: {
    errorMessage: undefined,
    errors: [],
    ...error,
  },
  meta: {
    receivedAt: Date.now(),
  },
});
